import { A } from "@solidjs/router";
import { For, JSX, Show } from "solid-js";
import { CategoryData } from "~/server/types/category";
import { getCategorySlugFromCategoryName } from "~/utils/common";
import { categoryIconMap } from "~/assets/category_icons";

export type CategoryStripV2Props = {
  categories: CategoryData[];
  selectedCategory?: string;
  showBrowseFirst?: boolean;
  onBrowseClick?: () => void;
};

/**
 * CategoryStripV2 Component
 * A horizontal scrollable category strip following Telescope design system
 * Based on Figma design: https://www.figma.com/design/uTQhl6K0EfVzl9uxl4Y4o1/Demand-master?node-id=240-4187
 */
export function CategoryStripV2(props: CategoryStripV2Props): JSX.Element {
  const BrowseIcon = categoryIconMap.BROWSE;
  return (
    <div class="box-border flex w-full flex-col gap-[10px] rounded-[16px] bg-background-mapping-reverse p-[6px]">
      <div class="relative w-full shrink-0 rounded-[12px] border border-solid border-stroke-1 bg-background-mapping-elevation-1">
        <div class="no-scrollbar box-border flex w-full items-center gap-[4px] overflow-x-auto overflow-y-clip p-[8px]">
          {/* Browse Category - First Item */}
          <Show when={props.showBrowseFirst}>
            <CategoryItem
              icon={<BrowseIcon class="size-[24px]" />}
              label="Browse"
              href="#"
              onClick={props.onBrowseClick}
            />
            <Divider />
          </Show>

          {/* Category Items */}
          <For each={props.categories}>
            {(category) => {
              const IconComponent = categoryIconMap[category.name];
              return (
                <CategoryItem
                  icon={
                    IconComponent ? (
                      <IconComponent class="size-[24px] " />
                    ) : (
                      <img
                        src={category.metadata.iconUrl}
                        alt={category.metadata.title}
                        class="block size-[24px] max-w-none"
                      />
                    )
                  }
                  label={category.metadata.title}
                  href={`/shop-by-category/${getCategorySlugFromCategoryName(category.name)}`}
                  isSelected={
                    props.selectedCategory?.toUpperCase() ===
                    category.name.toUpperCase()
                  }
                />
              );
            }}
          </For>
        </div>
      </div>
    </div>
  );
}

/**
 * Individual Category Item
 */
function CategoryItem(props: {
  icon: JSX.Element;
  label: string;
  href: string;
  isSelected?: boolean;
  onClick?: () => void;
}): JSX.Element {
  const content = (
    <div
      class="box-border flex w-[78px] shrink-0 flex-col items-center gap-[6px] px-0 py-[4px] transition-transform duration-200 hover:scale-105"
      onClick={props.onClick}
    >
      {/* Icon */}
      <div
        class="relative size-[24px] shrink-0"
        classList={{
          "stroke-text-normal-primary": props.isSelected,
          "stroke-text-normal-secondary": !props.isSelected,
        }}
      >
        {props.icon}
      </div>

      {/* Label */}
      <div class="flex w-full flex-col justify-center overflow-hidden overflow-ellipsis whitespace-nowrap text-center text-label-medium ">
        <p
          classList={{
            "text-text-normal-primary": props.isSelected,
            "text-text-normal-secondary": !props.isSelected,
          }}
          class="overflow-hidden overflow-ellipsis"
        >
          {props.label}
        </p>
      </div>
    </div>
  );

  return props.onClick ? (
    <div class="cursor-pointer">{content}</div>
  ) : (
    <A href={props.href}>{content}</A>
  );
}

/**
 * Vertical Divider
 */
function Divider(): JSX.Element {
  return (
    <div class="flex h-auto flex-row items-center self-stretch">
      <div class="h-full w-px shrink-0 bg-stroke-1" />
    </div>
  );
}
